import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, AlertTriangle, Check, Zap, AlertCircle, Wallet } from "lucide-react";
import { useCopies } from "../../hooks/useCopies";
import { useAuth } from "../../context/AuthContext";

const presets = [10, 50, 100, 250, 500, 1000];
const multipliers = [
  { value: 0.5, label: "0.5x", desc: "Lower risk" },
  { value: 1, label: "1x", desc: "Standard" },
  { value: 2, label: "2x", desc: "Aggressive" },
];

const CopyModal = ({ trader, open, onClose, onCopied }) => {
  const { addCopy, totalAllocated } = useCopies();
  const { user, refreshMe } = useAuth();
  // Use string state so the input can be cleared / typed naturally
  const [amountStr, setAmountStr] = useState("");
  const [multiplier, setMultiplier] = useState(1);
  const [stopLoss, setStopLoss] = useState(20);
  const [step, setStep] = useState("form");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const availableBalance = Math.max(0, Number(user?.balance || 0) - totalAllocated);
  const amount = Number(amountStr);
  const minCopy = Number(trader?.minCopy || 10);

  useEffect(() => {
    if (open && trader) {
      // Sensible default: at least the min, capped at available (so it doesn't auto-fail validation)
      const defaultAmt = Math.min(
        Math.max(minCopy, 50),
        availableBalance > 0 ? availableBalance : Math.max(minCopy, 50)
      );
      setAmountStr(String(defaultAmt));
      setMultiplier(1);
      setStopLoss(20);
      setStep("form");
      setErr(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trader]);

  if (!open || !trader) return null;

  const noFunds = availableBalance <= 0;
  const tooLow = !noFunds && amountStr !== "" && amount > 0 && amount < minCopy;
  const tooHigh = !noFunds && amount > availableBalance;
  const empty = amountStr === "" || amount <= 0;
  const valid = !noFunds && !tooLow && !tooHigh && !empty;

  let inlineMsg = null;
  if (noFunds) {
    inlineMsg = "You need to deposit funds before you can copy a trader.";
  } else if (empty) {
    inlineMsg = "Enter an amount to allocate.";
  } else if (tooLow) {
    inlineMsg = `Minimum copy amount for ${trader.name} is $${minCopy}.`;
  } else if (tooHigh) {
    inlineMsg = `Amount exceeds your available balance ($${availableBalance.toFixed(2)}).`;
  }

  const handleConfirm = async () => {
    if (!valid) return;
    setSubmitting(true);
    setErr(null);
    try {
      await addCopy(trader, amount, multiplier);
      await refreshMe();
      setStep("success");
      if (onCopied) onCopied();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start copy. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="copy-modal">
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      ></div>

      <div className="relative w-full max-w-md bb-card rounded-3xl overflow-hidden shadow-2xl animate-fadeIn">
        {step === "form" ? (
          <>
            <div className="flex items-start justify-between p-5 lg:p-6 border-b border-white/5">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center text-[14px] font-bold text-white shrink-0"
                  style={{ background: `linear-gradient(135deg, ${trader.color}, ${trader.accent})` }}
                >
                  {trader.avatar}
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-white/45">Copy trader</div>
                  <div className="font-display font-bold text-[18px] text-white truncate">{trader.name}</div>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="copy-modal-close"
                className="w-8 h-8 rounded-lg text-white/55 hover:text-white hover:bg-white/[0.05] flex items-center justify-center shrink-0"
              >
                <X size={17} />
              </button>
            </div>

            <div className="p-5 lg:p-6 space-y-5">
              {noFunds && (
                <div
                  data-testid="copy-modal-no-funds"
                  className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-[12px] text-amber-200"
                >
                  <Wallet size={16} className="shrink-0 mt-0.5 text-amber-300" />
                  <div className="flex-1">
                    <div className="font-semibold text-amber-100 mb-0.5">No funds available</div>
                    <div className="text-amber-200/85 leading-relaxed">
                      Deposit funds first, then come back to allocate to {trader.name}.
                    </div>
                    <Link
                      to="/dashboard/funds"
                      onClick={onClose}
                      data-testid="copy-modal-deposit-link"
                      className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-50 text-[12px] font-medium transition-colors"
                    >
                      Go to deposit <Wallet size={12} />
                    </Link>
                  </div>
                </div>
              )}

              {/* Allocation */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12px] text-white/55">Allocation</span>
                  <span className="text-[11px] text-white/40 tabular-nums" data-testid="copy-modal-available">
                    Available ${availableBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/55 text-[18px]">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amountStr}
                    onChange={(e) => {
                      const v = e.target.value;
                      // Allow only digits + a single decimal
                      if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                        setAmountStr(v);
                      }
                    }}
                    placeholder="0"
                    disabled={noFunds}
                    data-testid="copy-modal-amount-input"
                    className="w-full h-14 pl-10 pr-4 bg-white/[0.02] border border-white/8 rounded-xl text-[22px] font-display font-bold text-white outline-none focus:border-[#1c3df5] tabular-nums disabled:opacity-50"
                  />
                </div>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  {presets.map((a) => {
                    const disabledPreset = noFunds || a > availableBalance;
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => setAmountStr(String(a))}
                        disabled={disabledPreset}
                        data-testid={`copy-modal-preset-${a}`}
                        className={`px-2.5 py-1 rounded-lg text-[11px] border transition-all ${
                          amount === a && !disabledPreset
                            ? "border-[#1c3df5] bg-[#1c3df5]/15 text-white"
                            : "border-white/8 text-white/55 hover:text-white hover:border-white/20"
                        } ${disabledPreset ? "opacity-40 cursor-not-allowed hover:text-white/55 hover:border-white/8" : ""}`}
                      >
                        ${a >= 1000 ? `${a / 1000}k` : a}
                      </button>
                    );
                  })}
                  {!noFunds && availableBalance > 0 && (
                    <button
                      type="button"
                      onClick={() => setAmountStr(String(Math.floor(availableBalance * 100) / 100))}
                      data-testid="copy-modal-preset-max"
                      className="px-2.5 py-1 rounded-lg text-[11px] border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20 transition-all"
                    >
                      Max
                    </button>
                  )}
                </div>
                <div className="text-[10px] text-white/40 mt-2">
                  Min copy amount: ${minCopy}
                </div>
              </div>

              {/* Multiplier */}
              <div>
                <div className="text-[12px] text-white/55 mb-2">Risk multiplier</div>
                <div className="grid grid-cols-3 gap-2">
                  {multipliers.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setMultiplier(m.value)}
                      data-testid={`copy-modal-multiplier-${m.value}`}
                      className={`p-3 rounded-xl border text-center transition-all ${
                        multiplier === m.value
                          ? "border-[#1c3df5] bg-[#1c3df5]/15"
                          : "border-white/8 hover:border-white/20"
                      }`}
                    >
                      <div className="font-display font-bold text-[16px] text-white">{m.label}</div>
                      <div className="text-[10px] text-white/45 mt-0.5">{m.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Stop-loss */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12px] text-white/55">Auto-stop if loss exceeds</span>
                  <span className="text-[13px] text-[#7d8eff] font-medium tabular-nums">{stopLoss}%</span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="50"
                  step="5"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(Number(e.target.value))}
                  data-testid="copy-modal-stoploss-slider"
                  className="w-full accent-[#1c3df5]"
                />
                <div className="flex justify-between text-[10px] text-white/35 mt-1">
                  <span>5%</span>
                  <span>50%</span>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-white/[0.02] border border-white/8 rounded-xl p-4 space-y-2 text-[12px]">
                <div className="flex justify-between">
                  <span className="text-white/55">You allocate</span>
                  <span className="text-white tabular-nums font-medium">
                    ${(amount || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/55">Trade size</span>
                  <span className="text-white tabular-nums font-medium">{multiplier}x trader's volume</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/55">Max loss before stop</span>
                  <span className="text-rose-400 tabular-nums font-medium">
                    ${(((amount || 0) * stopLoss) / 100).toFixed(0)}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-2 text-[11px] text-white/45 leading-relaxed">
                <AlertTriangle size={13} className="text-amber-400 shrink-0 mt-0.5" />
                <span>
                  Past performance does not guarantee future results. Your capital is at risk.
                  This is a demo — no real funds are moved.
                </span>
              </div>

              {inlineMsg && !err && (
                <div
                  data-testid="copy-modal-inline-message"
                  className={`flex items-start gap-2 px-3 py-2.5 rounded-lg text-[12px] ${
                    noFunds || tooHigh || tooLow
                      ? "bg-rose-500/10 border border-rose-500/30 text-rose-300"
                      : "bg-white/[0.04] border border-white/10 text-white/60"
                  }`}
                >
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <span>{inlineMsg}</span>
                </div>
              )}

              {err && (
                <div
                  data-testid="copy-modal-error"
                  className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300"
                >
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <span>{err}</span>
                </div>
              )}

              <button
                type="button"
                onClick={handleConfirm}
                disabled={!valid || submitting}
                data-testid="copy-modal-start-button"
                className={`w-full py-3.5 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${
                  valid && !submitting
                    ? "bb-blue"
                    : "bg-white/[0.05] text-white/40 cursor-not-allowed"
                }`}
              >
                <Zap size={16} /> {submitting ? "Starting…" : "Start copying"}
              </button>
            </div>
          </>
        ) : (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Check size={28} className="text-emerald-400" />
            </div>
            <div className="font-display font-bold text-[22px] text-white mb-2">
              Copying {trader.name}
            </div>
            <p className="text-white/55 text-[14px] mb-6">
              ${(amount || 0).toLocaleString()} allocated at {multiplier}x trade size.<br />
              Mirror trades will start within seconds.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="copy-modal-success-close"
              className="w-full py-3 rounded-xl bb-blue text-white font-medium"
            >
              Go to my copies
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CopyModal;
